import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, MapPin, Phone, Building2, Plus } from "lucide-react";
import { KineticLines, Reveal, Eyebrow } from "@/components/site/Reveal";
import { ProposalForm } from "@/components/site/ProposalForm";

const CONTACT_ITEMS = [
  { icon: MapPin, t: "Address", lines: ["30 N. Gould St. Ste R Sheridan,", "WY 82801"] },
  { icon: Mail, t: "Email", lines: ["sales@vyacomm-media.com"] },
  { icon: Phone, t: "Phone", lines: ["+1 307 2180235"] },
  { icon: Building2, t: "Corporate Hub", lines: ["India, US"] },
];

const FAQS = [
  { q: "How quickly can Vyacomm Media launch a new campaign?", a: "Once campaign specifications, asset links, and targeting parameters are approved, campaigns typically go live within 3 to 5 business days. Real-time delivery can be configured via API, webhook, or scheduled secure batch drops." },
  { q: "Can Vyacomm Media work on a white-label basis for agencies?", a: "Absolutely. Over 45% of our monthly volume is delivered on behalf of leading global B2B media agencies. We provide full white-label reporting, unbranded asset delivery, and strict agency confidentiality." },
  { q: "How do you verify that leads are genuine opt-ins?", a: "Every lead includes time-stamped digital consent, the originating asset title, IP address verification, and telephone validation logs. We maintain full GDPR/CCPA audit trails for every lead generated." },
  { q: "What is your policy on invalid or unresponsive leads?", a: "We provide an ironclad 100% replacement guarantee. Any record failing agreed firmographic, title, or contact validity parameters will be audited and replaced within 14 days of delivery at no extra charge." },
  { q: "What pricing models do you support?", a: "We operate primarily on a pure Cost-Per-Lead (CPL) performance model. You only pay for leads that meet your strict ICP criteria and pass our multi-tier verification process." },
];

const Faq = ({ q, a, i }) => {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="rounded-2xl border border-line bg-white" data-testid={`faq-item-${i + 1}`}>
      <button
        data-testid={`faq-toggle-${i + 1}`}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
      >
        <span className="font-jakarta text-base font-bold text-navy">{q}</span>
        <motion.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.3 }} className="shrink-0 text-cobalt">
          <Plus className="h-5 w-5" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="px-6 pb-6 text-sm leading-relaxed text-slate-600">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Contact() {
  useEffect(() => {
    document.title = "Contact Vyacomm Media | Inquire About B2B Lead Programs";
  }, []);

  return (
    <div data-testid="contact-page">
      <section className="grain hero-grid-lines relative overflow-hidden bg-navy pb-20 pt-[200px]" data-testid="contact-hero">
        <div className="pointer-events-none absolute -left-40 top-16 h-[420px] w-[420px] rounded-full bg-cobalt/20 blur-[140px]" />
        <div className="relative z-10 mx-auto max-w-[1808px] px-5 sm:px-8 lg:px-12 2xl:px-14">
          <Reveal delay={0.05} y={16}>
            <Eyebrow dark>Contact & Schedule Discovery</Eyebrow>
          </Reveal>
          <h1 className="mt-6 max-w-4xl font-jakarta text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <KineticLines lines={["Let's Build Your", "High-Velocity Revenue Pipeline."]} accentIndex={1} />
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              Whether you're generating 50 enterprise BANT leads a month or syndicating content across 50,000 global tech
              leaders, our senior strategists are ready to engineer a custom program.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-canvas py-24 sm:py-32" data-testid="contact-form-section">
        <div className="mx-auto grid max-w-[1808px] gap-14 px-5 sm:px-8 lg:px-12 2xl:px-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Eyebrow>What Happens After You Submit</Eyebrow>
              <div className="mt-8 space-y-6">
                {[
                  "Dedicated Account Strategist assigned within 2 business hours",
                  "Custom TAM count and CPL pricing delivered within 24 hours",
                  "Zero spam policy — your details are never resold or shared",
                ].map((s, i) => (
                  <div key={s} className="flex items-start gap-4">
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-navy font-mono2 text-xs font-semibold text-flame">
                      {i + 1}
                    </span>
                    <p className="pt-2 text-sm font-medium leading-relaxed text-charcoal">{s}</p>
                  </div>
                ))}
              </div>

              <div className="mt-10 space-y-4">
                <h3 className="font-jakarta text-lg font-bold text-navy" data-testid="get-in-touch-heading">
                  Get In Touch
                </h3>
                {CONTACT_ITEMS.map((c) => (
                  <div
                    key={c.t}
                    data-testid={`contact-item-${c.t.toLowerCase().replace(/\s+/g, "-")}`}
                    className="flex items-start gap-4 rounded-2xl border border-line bg-white p-5"
                  >
                    <c.icon className="mt-0.5 h-5 w-5 shrink-0 text-cobalt" />
                    <div>
                      <p className="text-sm font-bold text-navy">{c.t}</p>
                      {c.lines.map((l) => (
                        <p key={l} className="mt-0.5 text-sm text-slate-600">
                          {l}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.12}>
              <div className="rounded-3xl border border-line bg-white p-7 shadow-[0_30px_80px_-40px_rgba(11,25,44,0.35)] sm:p-10">
                <p className="font-mono2 text-[10px] tracking-[0.24em] text-cobalt">REQUEST CAMPAIGN FEASIBILITY & AUDIENCE COUNT</p>
                <h2 className="mt-3 font-jakarta text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">
                  Generate your custom proposal
                </h2>
                <p className="mt-2 text-sm text-slate-500">
                  Submit your parameters below — our strategy team delivers a free custom TAM count and program proposal
                  within 24 business hours.
                </p>
                <div className="mt-8">
                  <ProposalForm testIdPrefix="contact-proposal" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-line bg-white py-24 sm:py-32" data-testid="faq-section">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 lg:px-12 2xl:px-14">
          <Reveal>
            <Eyebrow>Frequently Asked Questions</Eyebrow>
            <h2 className="mt-4 font-jakarta text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Answers before you even ask
            </h2>
          </Reveal>
          <div className="mt-12 space-y-4">
            {FAQS.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.06}>
                <Faq q={f.q} a={f.a} i={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
